type FacetInput = {
  primary?: unknown;
  secondary?: unknown;
  count?: unknown;
};

export type JobSpecialization = {
  /** Original API value. Use this value when constructing filter URLs. */
  value: string;
  label: string;
  count: number;
};

export type JobCategory = {
  /** Original API value. Use this value when constructing filter URLs. */
  value: string;
  label: string;
  count: number;
  specializations: JobSpecialization[];
};

const displayNames: Record<string, string> = {
  business: "Business",
  "education & training": "Education & Training",
  "finance & legal": "Finance & Legal",
  hospitality: "Hospitality",
  "hr & people ops": "HR & People Ops",
  marketing: "Marketing",
  "operations & supply chain": "Operations & Supply Chain",
  sales: "Sales",
  technology: "Technology",
};

function normalizedName(value: string): string {
  return value.trim().replace(/\s+/g, " ").toLowerCase();
}

function displayName(value: string): string {
  const normalized = normalizedName(value);
  return displayNames[normalized] || value.trim().replace(/\s+/g, " ");
}

function countFrom(value: unknown): number {
  const numeric =
    typeof value === "number"
      ? value
      : typeof value === "string" && value.trim() !== ""
        ? Number(value)
        : Number.NaN;
  return Number.isFinite(numeric) && numeric > 0 ? numeric : 0;
}

function compareByCountThenName<T extends { count: number; label: string; value: string }>(a: T, b: T): number {
  if (a.count !== b.count) return b.count - a.count;
  const normalizedA = normalizedName(a.label);
  const normalizedB = normalizedName(b.label);
  if (normalizedA < normalizedB) return -1;
  if (normalizedA > normalizedB) return 1;
  if (a.value < b.value) return -1;
  if (a.value > b.value) return 1;
  return 0;
}

/**
 * Converts the flat job-board facet response into category cards. API values
 * remain intact for filtering; only labels are normalized for presentation.
 */
export function groupFunctionalAreaFacets(facets: readonly FacetInput[] | unknown): JobCategory[] {
  if (!Array.isArray(facets)) return [];

  const categories = new Map<string, { value: string; count: number; specializations: Map<string, number> }>();

  for (const facet of facets) {
    if (!facet || typeof facet !== "object") continue;
    const primary = typeof facet.primary === "string" && facet.primary.trim() ? facet.primary : null;
    const count = countFrom(facet.count);
    if (!primary || count === 0) continue;

    const category = categories.get(primary) || { value: primary, count: 0, specializations: new Map<string, number>() };
    category.count += count;

    const secondary = typeof facet.secondary === "string" && facet.secondary.trim() ? facet.secondary : null;
    if (secondary) category.specializations.set(secondary, (category.specializations.get(secondary) || 0) + count);
    categories.set(primary, category);
  }

  return [...categories.values()]
    .filter((category) => category.count > 0)
    .map((category) => ({
      value: category.value,
      label: displayName(category.value),
      count: category.count,
      specializations: [...category.specializations.entries()]
        .map(([value, count]) => ({ value, label: displayName(value), count }))
        .sort(compareByCountThenName),
    }))
    .sort(compareByCountThenName);
}

export function functionalAreaJobsUrl(primary: string, secondary?: string): string {
  const query = new URLSearchParams({ functionalArea: primary });
  if (secondary) query.set("secondaryFunctionalArea", secondary);
  return `/jobs?${query.toString()}`;
}

export function jobCountLabel(count: number): string {
  return `${count} open job${count === 1 ? "" : "s"}`;
}
